/**
 * `window.__ab`, the test hook (ADR 0029): present only in a development build
 * or with `?test=1`. Playwright drives the run and reads the renderer's
 * numbers through it; console errors and unhandled rejections are collected
 * in `errors`. Since slice 6b the page moves between a scene and a run: the
 * hook of the mode shown is present (`__ab` in a run, `__abScene` in a scene),
 * the other is removed, and `__abApp` says which mode shows.
 */
import type { Command, Event, Pos, RunState } from '@ab/game-core';
import type { MaterialProbe, OcclusionProbe, QualityTier, SceneAbFlags, SpriteState, ViewStats } from '@ab/renderer';
import { type Locale, type Strings, localeStrings } from '@ab/session';
import { pack, strings as tables, towns } from 'virtual:ab-content';
import { type SceneState, interactableNear } from '@ab/scene';
import { uiTiming } from '@ab/ui';
import type { Controller, ReplayReport, SendOutcome, SpritesReport } from './controller.ts';
import type { Flow, FlowMode } from './flow.ts';
import type { AccountStatus, AccountStore, MailState, RunCheck, ShipGuest, ShipState } from './account.ts';
import type { SceneController, SceneOverlay } from './scene-controller.ts';
import { bannerTypes, labelledTypes } from './overlays.ts';
import { setBagSlotsForTest } from './pack.ts';

export type TestHook = {
  state(): RunState;
  lines(): readonly string[];
  send(command: Command): Promise<SendOutcome>;
  /** True while a command's events are playing; keys pressed then are ignored. */
  busy(): boolean;
  stats(): ViewStats;
  eventCounts(): Readonly<Partial<Record<Event['type'], number>>>;
  /** 1 is normal speed; 0 makes every animation instant. */
  setSpeed(speed: number): void;
  replay(logText: string): Promise<ReplayReport>;
  /** The renderer's tier, and switching it at runtime (not remembered: that is the player's choice). */
  tier(): QualityTier;
  setTier(tier: QualityTier): void;
  /** Shows floor `floorNumber` as entered, the leader moved to `at` (captures; the session is not touched). */
  preview(floorNumber: number, at?: Pos): RunState;
  /** Disposes the view and mounts a new one; returns what the old one left on the GPU. */
  remount(tier: QualityTier): { readonly geometries: number; readonly textures: number };
  /** Plays events no command made (a case no golden log has), through the shell's overlays and the view. */
  playScripted(events: readonly Event[], before: RunState, after: RunState): Promise<void>;
  /** Where the view projects a unit's label anchor now. */
  project(unitId: string): { readonly x: number; readonly y: number; readonly visible: boolean } | null;
  /** Starts the view's peak draws and triangles again. */
  resetPeak(): void;
  /** Labels and banners the shell has made, by event type. */
  overlayCounts(): { readonly labels: Readonly<Record<string, number>>; readonly banners: Readonly<Record<string, number>> };
  /** The event types that always make a label, and those that make a banner. */
  readonly labelledTypes: readonly string[];
  readonly bannerTypes: readonly string[];
  /** The sprite sheets (slice 3): ids the view loaded, each one's origin (shipped or the dev fallback), and how many loads are pending. */
  sprites(): SpritesReport;
  /** Units drawn as sprites now (`stats().sprites.unitsDrawn`). */
  unitsDrawnAsSprites(): number;
  /** A sprite unit's anim, drawn facing, flip and frame index now; null for a proxy. */
  spriteState(unitId: string): SpriteState | null;
  /** Calls `listener` as the view starts each event (returns the unsubscribe). */
  watchEvents(listener: (event: Event) => void): () => void;
  /** Where the view projects a tile's centre on the ground now (CSS pixels of the canvas). */
  projectTile(pos: Pos): { readonly x: number; readonly y: number; readonly visible: boolean };
  /** The explored tile under a point of the canvas, or null (off the floor, or in the fog). */
  pickTile(x: number, y: number): Pos | null;
  /** The explored tiles of the floor on screen, per tile index. */
  explored(): readonly boolean[];
  /** Slice 4's touch play: the running route's target, the commands game-core accepted, sends refused as busy. */
  routing(): Pos | null;
  commands(): readonly Command[];
  busyRefusals(): number;
  /** How many commands game-core refused (each puts a line in the log). */
  refusals(): number;
  /** How many times the shell resized the view to a settled viewport size. */
  resizes(): number;
  /**
   * The minimap's canvas read back: pixels drawn (not transparent), and those
   * of the leader dot's colour (`--ab-map-leader`, within a small tolerance).
   */
  minimap(): { readonly filled: number; readonly leader: number };
  /** Loses and restores the canvas's WebGL context (`WEBGL_lose_context`), as a phone browser may. */
  loseContext(): boolean;
  restoreContext(): boolean;
  /** The locale the page shows now, and the string table a locale shows (its text over English for an untranslated key; slice 5). */
  locale(): Locale;
  strings(locale: Locale): Strings;
  readonly errors: string[];
};

declare global {
  interface Window {
    __ab?: TestHook;
  }
}

/** Starts collecting errors at once, before the app boots. */
export const captureErrors = (): string[] => {
  const errors: string[] = [];
  window.addEventListener('error', (e) => errors.push(`error: ${e.message}`));
  window.addEventListener('unhandledrejection', (e) => errors.push(`unhandled rejection: ${String(e.reason)}`));
  const original = console.error.bind(console);
  console.error = (...args: unknown[]) => {
    errors.push(`console.error: ${args.map(String).join(' ')}`);
    original(...args);
  };
  return errors;
};

/** How far a pixel's channels may be from the leader dot's colour and still count as the dot (antialiasing aside). */
const dotTolerance = 12;

const minimapPixels = (): { readonly filled: number; readonly leader: number } => {
  const c = document.querySelector<HTMLCanvasElement>('[data-testid="minimap"] canvas');
  const ctx = c?.getContext('2d') ?? null;
  if (c === null || ctx === null || c.width === 0 || c.height === 0) return { filled: 0, leader: 0 };
  const hex = getComputedStyle(c).getPropertyValue('--ab-map-leader').trim();
  const dot = [1, 3, 5].map((i) => Number.parseInt(hex.slice(i, i + 2), 16));
  const data = ctx.getImageData(0, 0, c.width, c.height).data;
  let filled = 0;
  let leader = 0;
  for (let i = 0; i < data.length; i += 4) {
    if ((data[i + 3] ?? 0) === 0) continue;
    filled += 1;
    if (dot.every((v, k) => Math.abs((data[i + k] ?? 0) - v) <= dotTolerance)) leader += 1;
  }
  return { filled, leader };
};

export const exposeHook = (controller: Controller, errors: string[], canvas: HTMLCanvasElement): void => {
  delete window.__abScene;
  // Taken before any loss: a lost context gives no extension object.
  const gl = canvas.getContext('webgl2') ?? canvas.getContext('webgl');
  const lose = gl?.getExtension('WEBGL_lose_context') ?? null;
  window.__ab = {
    state: () => controller.snapshot().state,
    lines: () => controller.snapshot().lines,
    send: (command) => controller.send(command),
    busy: () => controller.busy(),
    stats: () => controller.view.stats(),
    eventCounts: () => controller.view.eventCounts(),
    setSpeed: (speed) => {
      controller.setSpeed(speed);
    },
    replay: (logText) => controller.replay(logText),
    tier: () => controller.view.tier,
    setTier: (tier) => {
      controller.setTier(tier);
    },
    remount: (tier) => controller.remount(tier),
    preview: (floorNumber, at) => controller.preview(floorNumber, at),
    overlayCounts: () => controller.overlayCounts(),
    playScripted: (events, before, after) => controller.playScripted(events, before, after),
    project: (unitId) => controller.view.project(unitId),
    resetPeak: () => {
      controller.view.resetPeak();
    },
    labelledTypes,
    bannerTypes,
    sprites: () => controller.sprites(),
    unitsDrawnAsSprites: () => controller.view.stats().sprites.unitsDrawn,
    spriteState: (unitId) => controller.view.spriteState(unitId),
    watchEvents: (listener) => controller.watchEvents(listener),
    projectTile: (pos) => controller.view.projectTile(pos),
    pickTile: (x, y) => controller.view.pickTile(x, y),
    explored: () => controller.view.explored(),
    routing: () => controller.routing(),
    commands: () => controller.commands(),
    busyRefusals: () => controller.busyRefusals(),
    refusals: () => controller.refusals(),
    resizes: () => controller.resizes(),
    minimap: () => minimapPixels(),
    loseContext: () => {
      lose?.loseContext();
      return lose !== null;
    },
    restoreContext: () => {
      lose?.restoreContext();
      return lose !== null;
    },
    locale: () => controller.snapshot().locale,
    strings: (locale) => localeStrings(tables, locale),
    errors,
  };
};

/** What the scene hook says of a walker (a plain copy, for Playwright). */
export type SceneWalker = { readonly x: number; readonly y: number; readonly h: number; readonly facing: string; readonly moving: boolean; readonly running: boolean; readonly route: number };

/**
 * `window.__abScene`, the test hook of a walkable scene (slice 6a): present
 * only where `window.__ab` would be. It reads the walking model and the scene
 * view, and drives input as the keys, the pad and a tap do.
 */
export type SceneTestHook = {
  sceneId(): string;
  player(): SceneWalker;
  /** The residents: a crew member's presence and post now (ADR 0033); a town's are always present, at no post. */
  residents(): readonly (SceneWalker & { readonly human: string; readonly present: boolean; readonly post: string | null })[];
  stats(): ViewStats;
  tier(): QualityTier;
  setTier(tier: QualityTier): void;
  remount(tier: QualityTier): { readonly geometries: number; readonly textures: number };
  /** A screen point of the ground (game axes) and of a character's head. */
  project(x: number, y: number, h: number): { readonly x: number; readonly y: number; readonly visible: boolean };
  projectActor(id: string): { readonly x: number; readonly y: number; readonly visible: boolean } | null;
  pickGround(x: number, y: number): { readonly x: number; readonly y: number; readonly h: number } | null;
  /** Whether a body may stand at a ground point (the walking model's rule). */
  canWalk(x: number, y: number): boolean;
  /** The walking model's ground at a point: its height and the surface it reads (null: on no surface). */
  ground(x: number, y: number): { readonly h: number; readonly surface: string | null };
  /** The interactable the traveler stands by (the walking model's `interactableNear`), or null. */
  interactable(): { readonly id: string; readonly kind: string; readonly target: string | null } | null;
  /** Turns the traveler where it stands. */
  face(facing: string): void;
  /** Crosses to a scene's spawn as a portal does. */
  travel(to: string, spawn: string): void;
  /** Animation frames the scene loop asked for since mount. */
  frameRequests(): number;
  /** The walking model's revision (the view's `stats().scene.revision` is the one it last drew). */
  revision(): number;
  /** A material of the built scene, probed (hooks, colour, bump, the map's image). */
  material(name: string): MaterialProbe | null;
  /** The contact occlusion probed at a canvas point (Medium). */
  probeOcclusion(x: number, y: number, ao: number): OcclusionProbe | null;
  /** Each flock's state. */
  flocks(): readonly { readonly id: string; readonly status: string }[];
  /** GPU A/B runs (`gpu-ab.spec.ts`): turns Medium systems off (false) or back on; the view's `setAb`. */
  ab(flags: Partial<SceneAbFlags>): void;
  /** A tap at a canvas point (CSS pixels): routes there, as a touch does. */
  tap(x: number, y: number): boolean;
  /** The route marker's target, or null. */
  routing(): { readonly x: number; readonly y: number; readonly h: number } | null;
  /** Puts the traveler at a point (captures), snapping the camera. */
  place(x: number, y: number): void;
  crossings(): number;
  fading(): boolean;
  sprites(): SpritesReport;
  spriteState(id: string): SpriteState | null;
  resizes(): number;
  /** Which surface textures show shipped artwork. */
  artwork(): Readonly<Record<string, boolean>>;
  loseContext(): boolean;
  restoreContext(): boolean;
  /** Slice 6b: the prompt's word key, what is open over the scene, whether the model is held, arrivals so far. */
  prompt(): string | null;
  overlay(): SceneOverlay | null;
  held(): boolean;
  arrival(): number;
  /** ADR 0032, 0033: the scene's time of day (a preset), its presets, and the destination while the airship flies (null otherwise). */
  lighting(): string;
  lightings(): readonly string[];
  flying(): string | null;
  /** Sets the time of day as the pause menu's row does, not remembered (captures, the GPU night row). */
  setLighting(lighting: string): void;
  /** A framing of the scene's camera (the airship's `overview` for captures; `street` or the scene's default back). */
  setFraming(name: string): void;
  /** The banner shown over the scene now (its key), and the place's name key. */
  banner(): string | null;
  nameKey(): string;
  /** Picks a row of the open course or egg menu, as a tap or Enter does (ADR 0033). */
  course(row: string): void;
  /**
   * The packing panel's bag size for this page (slice 8f: the content cannot fill the rules' slots); null: the rules'.
   * In a development build only (`import.meta.env.DEV`); the server's checks of the real slots are its own tests'.
   */
  readonly setBagSlots?: (n: number | null) => void;
  egg(row: string): void;
  /** Whether a hatch sequence plays now. */
  hatching(): boolean;
  /** Uses the interactable in reach, as E does. */
  action(): boolean;
  /** Opens a dialogue of string keys (every page of every locale measured at 390 px). */
  say(speaker: string | null, pages: readonly string[]): void;
  /** A town's market and the base price of each item (the shop test computes the prices itself). */
  market(town: string): { readonly market: readonly { readonly item: string; readonly buy?: number }[]; readonly basePrice: Readonly<Record<string, number>> } | null;
  locale(): Locale;
  strings(locale: Locale): Strings;
  readonly errors: string[];
};

declare global {
  interface Window {
    __abScene?: SceneTestHook;
    __abApp?: AppTestHook;
    __abShip?: ShipTestHook;
  }
}

/**
 * `window.__abShip` (slice 7a; slice 7b, ADR 0034): the account and the ship as the server last said them (the
 * eggs aboard, the jobs, the roster), the hatchlings standing in the rooms, the server's time as the page knows it,
 * and `refresh` (reads the account again; after a test moved the server's clock, `POST /api/test/clock`, the scene
 * on screen re-reads its incubators), and since slice 8c the mailbox. Nothing here changes what the server holds.
 */
export type ShipTestHook = {
  status(): AccountStatus;
  name(): string | null;
  state(): ShipState;
  guests(): readonly ShipGuest[];
  now(): number;
  refresh(): Promise<string | null>;
  /** The run the server keeps as far as the page knows (`account.ts` `RunCheck`'s kind). */
  runCheck(): RunCheck['kind'];
  /** A temporary password the page asks to replace (ADR 0038 D8). */
  mustChangePassword(): boolean;
  /** The mailbox as the server last said it (slice 8c). */
  mail(): MailState;
};

export const exposeShipHook = (account: AccountStore, mode: () => FlowMode): void => {
  window.__abShip = {
    status: () => account.status(),
    name: () => account.name(),
    mustChangePassword: () => account.mustChangePassword(),
    mail: () => account.mail(),
    state: () => account.state(),
    guests: () => account.guests(),
    now: () => account.time.now(),
    runCheck: () => account.run().kind,
    refresh: async () => {
      const error = await account.refresh();
      const m = mode();
      if (m.kind === 'scene') m.scene.refreshShip();
      return error;
    },
  };
};

/**
 * `window.__abApp` (slice 6b): which mode the page shows, the runs started from the airship's mooring, and what each
 * disposed view left on the GPU (the fix round's round-trip test: 0/0 each).
 */
export type AppTestHook = {
  mode(): 'scene' | 'run';
  runs(): number;
  disposals(): readonly { readonly geometries: number; readonly textures: number }[];
  /** Each server run's return on the page's clock: its end shown (`onEnd`) and the deck back (`Flow.returns`). */
  returns(): readonly { readonly endedAt: number; readonly backAt: number | null }[];
  /** The hold before the town comes back after a run, a banner's time on screen, a crossing's fade and the boarding banner's hold (`uiTiming`). */
  timing(): { readonly returnMs: number; readonly bannerMs: number; readonly fadeMs: number; readonly flightMs: number };
};

export const exposeAppHook = (flow: Flow): void => {
  window.__abApp = {
    mode: () => flow.snapshot().mode.kind,
    runs: () => flow.runs(),
    disposals: () => flow.disposals(),
    returns: () => flow.returns(),
    timing: () => ({ returnMs: uiTiming.returnMs, bannerMs: uiTiming.banner, fadeMs: uiTiming.sceneFadeMs, flightMs: uiTiming.flightMs }),
  };
};

export const exposeSceneHook = (controller: SceneController, errors: string[], canvas: HTMLCanvasElement): void => {
  delete window.__ab;
  const gl = canvas.getContext('webgl2') ?? canvas.getContext('webgl');
  const lose = gl?.getExtension('WEBGL_lose_context') ?? null;
  const walker = (w: SceneState['player']): SceneWalker => ({ x: w.x, y: w.y, h: w.h, facing: w.facing, moving: w.moving, running: w.running, route: w.route.length });
  window.__abScene = {
    sceneId: () => controller.snapshot().sceneId,
    player: () => walker(controller.state().player),
    residents: () => controller.state().residents.map((r) => ({ ...walker(r), human: r.human, present: r.present, post: r.post })),
    stats: () => controller.view.stats(),
    tier: () => controller.view.tier,
    setTier: (tier) => {
      controller.setTier(tier);
    },
    remount: (tier) => controller.remount(tier),
    project: (x, y, h) => controller.view.project(x, y, h),
    projectActor: (id) => controller.view.projectActor(id),
    pickGround: (x, y) => controller.view.pickGround(x, y),
    canWalk: (x, y) => controller.state().map.canWalk(x, y),
    ground: (x, y) => ({ h: controller.state().map.heightAt(x, y), surface: controller.state().map.surfaceAt(x, y) }),
    interactable: () => {
      const i = interactableNear(controller.state());
      return i === null ? null : { id: i.id, kind: i.kind, target: i.target ?? null };
    },
    face: (facing) => {
      const f = (['n', 'ne', 'e', 'se', 's', 'sw', 'w', 'nw'] as const).find((x) => x === facing);
      if (f !== undefined) controller.face(f);
    },
    travel: (to, spawn) => {
      controller.travel(to, spawn);
    },
    frameRequests: () => controller.frameRequests(),
    revision: () => controller.state().revision,
    material: (name) => controller.view.material(name),
    probeOcclusion: (x, y, ao) => controller.view.probeOcclusion(x, y, ao),
    flocks: () => controller.view.flocks(),
    ab: (flags) => {
      controller.view.setAb(flags);
    },
    tap: (x, y) => controller.tap(x, y),
    routing: () => controller.snapshot().route,
    place: (x, y) => {
      controller.place(x, y);
    },
    crossings: () => controller.crossings(),
    fading: () => controller.snapshot().fading,
    sprites: () => controller.sprites(),
    spriteState: (id) => controller.view.spriteState(id),
    resizes: () => controller.resizes(),
    artwork: () => ({ ...controller.view.artwork() }),
    prompt: () => controller.snapshot().prompt,
    overlay: () => controller.snapshot().overlay,
    held: () => controller.state().held,
    arrival: () => controller.snapshot().arrival,
    lighting: () => controller.snapshot().lighting,
    lightings: () => controller.snapshot().lightings,
    flying: () => controller.snapshot().flying,
    setLighting: (lighting) => {
      controller.setLighting(lighting);
    },
    setFraming: (name) => {
      controller.view.setFraming(name);
    },
    banner: () => controller.snapshot().banner?.key ?? null,
    nameKey: () => controller.snapshot().nameKey,
    course: (row) => {
      controller.course(row);
    },
    // A development build only (the Playwright tests run the dev server): a production page with `?test=1` has none.
    ...(import.meta.env.DEV
      ? {
          setBagSlots: (n: number | null) => {
            setBagSlotsForTest(n);
          },
        }
      : {}),
    egg: (row) => {
      controller.egg(row);
    },
    hatching: () => controller.snapshot().hatching,
    action: () => controller.action(),
    say: (speaker, pages) => {
      controller.say(speaker, pages);
    },
    market: (id) => {
      const town = towns[id];
      return town === undefined ? null : { market: town.market.map((m) => ({ item: m.item, ...(m.buy === undefined ? {} : { buy: m.buy }) })), basePrice: Object.fromEntries(Object.values(pack.items).map((i) => [i.id, i.basePrice])) };
    },
    locale: () => controller.snapshot().locale,
    strings: (locale) => localeStrings(tables, locale),
    loseContext: () => {
      lose?.loseContext();
      return lose !== null;
    },
    restoreContext: () => {
      lose?.restoreContext();
      return lose !== null;
    },
    errors,
  };
};
